import { EventTarget, defineEventAttribute } from 'event-target-shim'
import fetchRemoteTrack from '../WebVTT/fetchRemoteTrack'
import parseCues from '../WebVTT/parseCues'
import { TextTrackKind, TextTrackMode } from './enums'
import findActiveCues from './findActiveCues'

let uniqueTrackId = 0

/**
 * A representation of a single `TextTrack`.
 *
 * @see [Spec]{@link https://html.spec.whatwg.org/multipage/embedded-content.html#texttrack}
 * (This is not a full implementation of the spec)
 */
class TextTrack extends EventTarget {
  /**
   * @param {Object} options The TextTrack attributes
   * @param {EventTarget} player The player of other event target emitting `timeupdate` events
   */
  constructor(options, player) {
    super()

    let loaded = false
    let mode = TextTrackMode[options.mode] || 'disabled'

    if (options.kind === 'metadata' || options.kind === 'chapters') {
      mode = 'hidden'
    }

    const trackProps = {
      id: options.id || `track_${++uniqueTrackId}`,
      kind: TextTrackKind[options.kind] || 'subtitles',
      label: options.label || '',
      language: options.language || options.srcLang || '',
    }

    for (const key in trackProps) {
      Object.defineProperty(this, key, {
        get() {
          return trackProps[key]
        },
        set() {},
      })
    }

    // These should be `TextTrackCueList` but except for `getCueById` the interface
    // is the same; they are just not readonly, but that is fine.
    this.cues = []
    this.activeCues = []

    const loadTrackThenListen = () =>
      fetchRemoteTrack(options.src)
        .then(parseCues)
        .catch((error) => ({ cues: [], errors: [error] }))
        .then(({ cues, errors }) => {
          loaded = true
          this.cues = cues
          // Cues that parse with errors are simply omitted so are not critical.
          // > Not part of the spec: Errors are emitted for logging
          const track = { ...trackProps, src: options.src }
          this.dispatchEvent(new CustomEvent('trackloaded', { detail: { track, errors } }))
        })
        .then(() => player.addEventListener('timeupdate', updateActiveCues))

    const updateActiveCues = (ev) => {
      const currentTime = ev.target.currentTime || (ev.detail && ev.detail.currentTime) || 0
      const activeCues = findActiveCues(currentTime, this.cues, this.activeCues)
      if (activeCues !== this.activeCues) {
        this.activeCues = activeCues
        this.dispatchEvent(new CustomEvent('cuechange'))
      }
    }

    Object.defineProperties(this, {
      mode: {
        get() {
          return mode
        },
        set(newMode) {
          if (!TextTrackMode[newMode]) {
            return
          }

          mode = newMode

          if (mode === 'disabled') {
            player.removeEventListener('timeupdate', updateActiveCues)
          } else {
            loaded ? player.addEventListener('timeupdate', updateActiveCues) : loadTrackThenListen()
          }

          // > Not part of the spec: Allows the TextTrackList to act accordingly
          this.dispatchEvent(new CustomEvent('modechange'))
        },
      },
    })

    // Not part of the spec, but used to unbind all event listeners
    this.removeInternalListeners = () => player.removeEventListener('timeupdate', updateActiveCues)

    if (mode !== 'disabled') {
      loadTrackThenListen() // Listen for time updates from the start
    }
  }
}

defineEventAttribute(TextTrack.prototype, 'cuechange')

export default TextTrack
